import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { database } from "../../../firebaseConfig";
import { ref, onValue, get } from "firebase/database";
import OfferPreviewPage from "../OfferPreviewPage";
import "../OfferPreviewPage/OfferPreviewPage.css";
import "./CustomOfferView.css";
import { Helmet } from "react-helmet-async";

/**
 * CustomOfferView loads publicly shared offers via a customId
 * and uses OfferPreviewPage for display
 */
const CustomOfferView = () => {
  const { customId } = useParams();
  const [offerData, setOfferData] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metaData, setMetaData] = useState({
    title: "Custom Offer Details",
    description: "View the details of your tailored offer, including features, scope, and pricing — created to match your specific business requirements.",
    imageUrl: null
  });

  useEffect(() => {
    // Validate and extract userId and offerId from the customId
    if (!customId || !customId.includes("+")) {
      setError("Invalid offer link");
      setLoading(false);
      return;
    }

    const [userId, offerId] = customId.split("+");

    if (!userId || !offerId) {
      setError("Invalid offer link");
      setLoading(false);
      return;
    }

    const loadTeamMemberData = async (teamMemberId) => {
      try {
        const teamMemberRef = ref(database, `/World/${userId}/team/members/${teamMemberId}`);
        const snapshot = await get(teamMemberRef);
        if (snapshot.exists()) {
          return snapshot.val();
        }
        return null;
      } catch (error) {
        console.error("Error loading team member data:", error);
        return null;
      }
    };

    // Load offer from Firebase database
    const offerRef = ref(database, `/World/${userId}/offers/${offerId}`);
    
    const unsubscribe = onValue(offerRef, async (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        
        // Load team member data if available
        if (data.teamMemberId) {
          const teamMemberData = await loadTeamMemberData(data.teamMemberId);
          if (teamMemberData) {
            data.teamMember = teamMemberData;
          }
        }
        
        setOfferData(data);
        
        // Set meta data for social media preview
        setMetaData({
          title: data.title || `Offer: ${data.name || 'Custom Offer'}`,
          description: data.description || `A tailored offer${data.teamMember ? ` from ${data.teamMember.name}` : ''}`,
          imageUrl: data.imageUrl || (data.teamMember && data.teamMember.profileImageUrl) || null
        });
        
        // Load the associated services
        const servicesRef = ref(database, `/World/${userId}/services`);
        
        onValue(servicesRef, (servicesSnapshot) => {
          if (servicesSnapshot.exists() && data.services) {
            const servicesData = servicesSnapshot.val();
            const offerServices = data.services
              .map(serviceId => {
                const service = servicesData[serviceId];
                return service ? { id: serviceId, ...service } : null;
              })
              .filter(service => service !== null);
            
            setServices(offerServices);
          }
          setLoading(false);
        }, (error) => {
          console.error("Error loading services:", error);
          setLoading(false);
        });
      } else {
        setError("Offer not found");
        setLoading(false);
      }
    }, (error) => {
      console.error("Error loading offer:", error);
      setError("Offer could not be loaded");
      setLoading(false);
    });

    return () => unsubscribe();
  }, [customId]);

  // Show loader, error, or empty state
  if (loading) {
    return (
      <>
        <Helmet>
          <title>Loading offer...</title>
          <meta name="description" content="Please wait, the offer is loading." />
        </Helmet>
        <div className="offer-preview-page offer-preview-page--only-preview">
          <div className="offer-preview-page__loading">
            <div className="offer-preview-page__spinner"></div>
            <p>Loading offer...</p>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Helmet>
          <title>Error loading offer</title>
          <meta name="description" content="The offer could not be loaded." />
        </Helmet>
        <div className="offer-preview-page offer-preview-page--only-preview">
          <div className="offer-preview-page__error">
            <span className="offer-preview-page__error-icon">⚠️</span>
            <h3>Error</h3>
            <p>{error}</p>
          </div>
        </div>
      </>
    );
  }

  if (!offerData) {
    return (
      <>
        <Helmet>
          <title>Offer not found</title>
          <meta name="description" content="The requested offer is not available." />
        </Helmet>
        <div className="offer-preview-page offer-preview-page--only-preview">
          <div className="offer-preview-page__empty">
            <span className="offer-preview-page__empty-icon">📄</span>
            <h3>Offer not found</h3>
            <p>The requested offer is not available.</p>
          </div>
        </div>
      </>
    );
  }

  // Use the OfferPreviewPage component for display
  return (
    <>
      <Helmet>
        <title>{metaData.title}</title>
        <meta name="description" content={metaData.description} />
        
        {/* Open Graph Meta Tags for Facebook, WhatsApp etc. */}
        <meta property="og:title" content={metaData.title} />
        <meta property="og:description" content={metaData.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        {metaData.imageUrl && <meta property="og:image" content={metaData.imageUrl} />}
        
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaData.title} />
        <meta name="twitter:description" content={metaData.description} />
        {metaData.imageUrl && <meta name="twitter:image" content={metaData.imageUrl} />}
      </Helmet>
      
      <div className="custom-offer-view">
        <OfferPreviewPage 
          offerData={{
            ...offerData,
            teamMember: offerData.teamMember ? {
              ...offerData.teamMember,
              signature: offerData.teamMember.signature,
              signatureMaxWidth: offerData.teamMember.signatureMaxWidth || 150,
              name: offerData.teamMember.name,
              companyName: offerData.teamMember.companyName,
              companySlogan: offerData.teamMember.companySlogan,
              personalEmail: offerData.teamMember.personalEmail,
              generalEmail: offerData.teamMember.generalEmail,
              phone: offerData.teamMember.phone,
              website: offerData.teamMember.website,
              bankName: offerData.teamMember.bankName,
              iban: offerData.teamMember.iban,
              bic: offerData.teamMember.bic
            } : null
          }}
          services={services} 
          onlyPreview={true} 
        />
      </div>
    </>
  );
};

export default CustomOfferView; 